<template>
  <v-container>
    <v-row>
      <v-col cols="6">
        <v-card
          v-if="isSinglePaymentPlan"
          elevation="3"
          outlined
        >
          <v-card-title>
            Single Settlement Plan Details
            <v-spacer />
            <v-icon
              color="red"
              large
              @click="handleDisplaySettlementDeleteDialog"
            >
              mdi-delete
            </v-icon>
          </v-card-title>
          <v-card-text>
            <p>Amount: {{ singlePaymentData.paymentAmount | currency }}</p>
            <p>Payment Date: {{ singlePaymentData.paymentDate }}</p>
            <p>Payment Type: {{ singlePaymentData.paymentType }}</p>
            <p>Payment Cured: {{ singlePaymentData.paymentCured }}</p>
            <p>Payment Returned: {{ singlePaymentData.paymentReturned }}</p>
            <p>Settlement Created On: {{ singlePaymentData.settlementCreatedDate }}</p>
          </v-card-text>
        </v-card>
        <v-card
          v-if="isReducedPayoffPlan"
          elevation="3"
          outlined
        >
          <v-card-title>
            Reduced Payoff Plan Details
            <v-spacer />
            <v-icon
              color="red"
              large
              @click="handleDisplaySettlementDeleteDialog"
            >
              mdi-delete
            </v-icon>
          </v-card-title>
          <v-card-text>
            <p>
              {{ reducedPayoffPlanData.title }}
            </p>
            <p>
              {{ reducedPayoffPlanData.settlementTerm }} Months Term
            </p>
            <p>
              {{ reducedPayoffPlanData.paymentAmount | currency }}
              {{ reducedPayoffPlanData.paymentFrequency }} payments
            </p>
            <p>Periodic Payment Amount: {{ reducedPayoffPlanData.paymentAmount | currency }}</p>
            <p>Periodic Credit Amount: {{ reducedPayoffPlanData.creditAmount | currency }}</p>
            <p>First Payment Date: {{ reducedPayoffPlanData.firstPaymentDate }}</p>
            <p>Next Due Date: {{ reducedPayoffPlanData.nextPaymentDate }}</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="downloadPaymentSchedule">
              View Full Payment Schedule
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <DeleteSettlementDialog
      v-if="displayDeleteSettlementDialog"
      :ticket="ticket"
      :dialog-title="deleteDialogTitle"
      :dialog-message="deleteDialogMessage"
      :delete-settlement-endpoint="deleteSettlementEndpoint"
      @close-dialog="displayDeleteSettlementDialog = false"
    />
  </v-container>
</template>

<script>

import { DateTime } from 'luxon';
import DeleteSettlementDialog from '@/views/Tickets/PdsSettlement/DeleteSettlementDialog.vue';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'DisplayLatestSettlementPlan',
  components: {
    DeleteSettlementDialog,
  },
  props: {
    settlementType: {
      type: String,
      required: true,
    },
    modelData: {
      type: Object,
      required: true,
    },
    ticket: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      displayDeleteSettlementDialog: false,
    };
  },

  computed: {
    ...mapGetters('loanProLoan', ['nextPaymentInfo']),

    deleteDialogTitle() {
      return `Delete ${this.settlementTypeFullStr}`;
    },
    deleteSettlementEndpoint() {
      const deleteEndpointName = this.settlementType.replaceAll('_', '-');
      return `api/v1/support/ticket/${this.ticket.zendesk.id}/pds-settlement/${this.ticket.active_loan.id}/${deleteEndpointName}/${this.modelData.id}`;
    },
    deleteDialogMessage() {
      return `Deleting the ${this.settlementTypeFullStr} will put the borrower back into their previous status and delete any pending payments. Are you sure you want to delete?`;
    },
    settlementTypeFullStr() {
      return this.settlementType.replaceAll('_', ' ');
    },
    isSinglePaymentPlan() {
      return this.settlementType === 'single_payment_plan';
    },
    isReducedPayoffPlan() {
      return this.settlementType === 'reduced_payoff_plan';
    },
    singlePaymentData() {
      if (this.isSinglePaymentPlan) {
        return {
          paymentDate: DateTime.fromISO(this.modelData.payment_due_date)
            .toLocaleString(DateTime.DATE_SHORT),
          paymentType: this.modelData?.payment_type ?? 'Manual Payment',
          settlementCreatedDate: DateTime.fromISO(this.modelData.created_at)
            .toLocaleString(DateTime.DATE_SHORT),
          paymentCured: this.modelData?.cured ? 'YES' : 'NO',
          paymentReturned: this.modelData?.returned ? 'YES' : 'NO',
          paymentAmount: this.modelData.payment_amount,
        };
      }
      return null;
    },
    reducedPayoffPlanData() {
      if (this.isReducedPayoffPlan) {
        return {
          settlementTerm: this.modelData.settlement_term,
          firstPaymentDate: this.modelData.first_payment_due_date,
          nextPaymentDate: this.nextPaymentInfo.paymentDate,
          paymentFrequency: (this.modelData.payment_frequency).split('.')[2],
          paymentAmount: this.modelData.payment_amount,
          creditAmount: this.modelData.credit_per_payment_amount,
          settlementAmount: this.modelData.total_settlement_amount,
          title: `Option ${this.modelData.plan_option_number} - ${(100 - this.modelData.plan_discount_percent)}% of Payoff`,
        };
      }
      return null;
    },
  },

  methods: {
    ...mapActions('notification', ['setNotification']),

    handleDisplaySettlementDeleteDialog() {
      this.displayDeleteSettlementDialog = true;
    },
    downloadPaymentSchedule() {
      this.$axios
        .get(`api/v1/support/ticket/${this.ticket.zendesk.id}/pds-settlement/${this.ticket.active_loan.id}/reduced-payoff-plan/${this.modelData.id}/download-payment-schedule`,
          { responseType: 'blob' })
        .then((res) => {
          const date = DateTime.fromISO(this.modelData.created_at).toUTC().toFormat('yyyy-MM-dd');
          const blob = new Blob([res.data], { type: 'application/pdf' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `settlement-payment-schedule-${this.modelData.loan_id}-${this.modelData.id}-${date}.pdf`;
          link.click();
          link.remove();
        })
        .catch(() => {
          this.setNotification({
            message: 'There was an error downloading that settlement payment schedule.',
            color: 'red',
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
  .v-card {
    p {
      font-weight: 500;
    }
  }
</style>
